import { IconCircleX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { CompactEmptyState } from "./SectionLayout";
import { currencyLabel, formatMoney, keywordKey, moneyStep } from "./sponsored-brands-format";
import type { KeywordRow, SponsoredBrandsMoneyCapabilities } from "./sponsored-brands-model";

export function KeywordSuggestionTable({
  money,
  rows,
  selectedKeys,
  allSelected,
  onToggle,
  onToggleAll,
}: {
  money: SponsoredBrandsMoneyCapabilities;
  rows: KeywordRow[];
  selectedKeys: Set<string>;
  allSelected: boolean;
  onToggle: (row: KeywordRow, selected: boolean) => void;
  onToggleAll: (selected: boolean) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <Table data-slot="sb-keyword-suggestion-table" className="table-fixed">
      <TableHeader className="sticky top-0 bg-background">
        <TableRow>
          <TableHead className="w-[42%]">{t("sb.keyword.keywordColumn")}</TableHead>
          <TableHead className="w-[26%]">{t("sb.keyword.matchTypeColumn")}</TableHead>
          <TableHead className="w-[22%]">{t("sb.keyword.suggestedBid")}</TableHead>
          <TableHead
            data-slot="sb-keyword-select-column"
            className="sticky right-0 w-10 bg-muted/40 px-2"
          >
            <Checkbox
              aria-label={t("sb.keyword.selectAll")}
              checked={allSelected}
              disabled={rows.length === 0}
              onCheckedChange={(checked) => onToggleAll(checked === true)}
            />
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? (
          rows.map((row, index) => {
            const key = keywordKey(row.matchType, row.keyword);
            const label = t(`sb.keyword.match.${row.matchType}`);
            const previous = rows[index - 1];
            return (
              <TableRow key={row.id} className="group">
                <TableCell className="min-w-0">
                  {previous?.keyword === row.keyword ? null : (
                    <span className="block truncate" title={row.keyword}>
                      {row.keyword}
                    </span>
                  )}
                </TableCell>
                <TableCell>{row.matchType}</TableCell>
                <TableCell>
                  {formatMoney(row.suggestedBid ?? row.bid, money.currencyCode)}
                </TableCell>
                <TableCell className="sticky right-0 bg-background px-2 group-hover:bg-muted/50">
                  <Checkbox
                    aria-label={t("sb.keyword.selectSuggestion", {
                      keyword: row.keyword,
                      matchType: label,
                    })}
                    checked={selectedKeys.has(key)}
                    onCheckedChange={(checked) => onToggle(row, checked === true)}
                  />
                </TableCell>
              </TableRow>
            );
          })
        ) : (
          <TableRow>
            <TableCell colSpan={4}>
              <CompactEmptyState />
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export function TargetTable({
  money,
  rows,
  onRowsChange,
}: {
  money: SponsoredBrandsMoneyCapabilities;
  rows: KeywordRow[];
  onRowsChange: (rows: KeywordRow[]) => void;
}) {
  const { t } = useTranslation("campaignCreation");
  return (
    <div className="flex min-h-0 min-w-0 flex-col border-l border-border p-4">
      <div className="mb-2 flex shrink-0 justify-between">
        <p className="font-medium">{t("sb.common.added", { count: rows.length })}</p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={!rows.length}
          onClick={() => onRowsChange([])}
        >
          {t("sb.common.removeAll")}
        </Button>
      </div>
      <ScrollArea scrollFade scrollbarGutter className="min-h-0 flex-1">
        <Table className="min-w-[520px]">
          <TableHeader className="sticky top-0 bg-background">
            <TableRow>
              <TableHead>{t("sb.keyword.keywordAndGroups")}</TableHead>
              <TableHead>{t("sb.keyword.bid")}</TableHead>
              <TableHead>{t("sb.keyword.suggestedBid")}</TableHead>
              <TableHead>{t("sb.common.action")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length ? (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.matchType} {row.keyword}
                  </TableCell>
                  <TableCell>
                    <InputGroup className="w-28">
                      <InputGroupAddon>
                        <InputGroupText>{currencyLabel(money.currencyCode)}</InputGroupText>
                      </InputGroupAddon>
                      <InputGroupInput
                        aria-label={t("sb.keyword.bidFor", { keyword: row.keyword })}
                        type="number"
                        min={money.bid.min}
                        max={money.bid.max}
                        step={moneyStep(money.bid.decimals)}
                        value={row.bid}
                        onChange={(event) =>
                          onRowsChange(
                            rows.map((item) =>
                              item.id === row.id ? { ...item, bid: event.target.value } : item,
                            ),
                          )
                        }
                      />
                    </InputGroup>
                  </TableCell>
                  <TableCell>
                    {row.suggestedBid
                      ? formatMoney(row.suggestedBid, money.currencyCode)
                      : t("sb.keyword.noSuggestedBid")}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t("sb.common.remove", { name: row.keyword })}
                      onClick={() => onRowsChange(rows.filter((item) => item.id !== row.id))}
                    >
                      <IconCircleX />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4}>
                  <CompactEmptyState />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}
